
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV = [
  { label: 'Home',       href: '/' },
  { label: 'About',      href: '/about' },
  { label: 'Programmes', href: '/programmes' },
];

const SERVICES_ITEMS = [
  { label: 'Corporate Training',  href: '/corporate-training',icon: 'ri-building-2-line' },
  { label: 'SETA Compliance',     href: '/seta-compliance',   icon: 'ri-shield-check-line' },
];

const RESOURCE_ITEMS = [
  { label: 'Resources',     href: '/resources',    icon: 'ri-folder-open-line' },
  { label: 'FAQ',           href: '/faq',          icon: 'ri-question-answer-line' },
];

export default function Header() {
  const [scrolled,          setScrolled]          = useState(false);
  const [menuOpen,          setMenuOpen]           = useState(false);
  const [servicesOpen,      setServicesOpen]       = useState(false);
  const [resourcesOpen,     setResourcesOpen]      = useState(false);
  const location = useLocation();
  const servicesRef  = useRef<HTMLDivElement>(null);
  const resourcesRef = useRef<HTMLDivElement>(null);

  /* scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* close everything on route change */
  useEffect(() => {
    setMenuOpen(false); setServicesOpen(false); setResourcesOpen(false);
  }, [location.pathname]);

  /* escape key */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setServicesOpen(false); setResourcesOpen(false); setMenuOpen(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* click outside */
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (servicesRef.current  && !servicesRef.current.contains(e.target as Node))  setServicesOpen(false);
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) setResourcesOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  /* body scroll lock */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isActive = (href: string) => location.pathname === href;
  const servicesActive = ['/corporate-training', '/seta-compliance'].includes(location.pathname);
  const resourcesActive = ['/resources', '/faq', '/testimonials'].includes(location.pathname);

  const linkCls = (active: boolean) =>
    `px-3 py-1.5 text-[0.875rem] font-medium rounded transition-colors duration-150 ${
      active ? 'text-[#2D7F3E] font-semibold' : 'text-[#3d3f42] hover:text-[#2D7F3E]'
    }`;

  const dropdownLinkCls = (active: boolean) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg transition-colors ${
      active ? 'bg-[#f0f9f2] text-[#2D7F3E] font-semibold' : 'text-[#3d3f42] hover:bg-[#f8f9fa] hover:text-[#2D7F3E]'
    }`;

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
        scrolled ? 'shadow-[0_1px_16px_rgba(0,0,0,0.08)]' : 'border-b border-[#e8eaea]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[68px]">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/0d5119b9e0bc6c06e786accc19d4f477/6e474aa7940fa2c996eaf1936cd3c4a1.png"
              alt="Sylman Academy"
              className="h-9 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center" aria-label="Main navigation">
            {NAV.map(n => (
              <Link key={n.href} to={n.href} className={linkCls(isActive(n.href))}>{n.label}</Link>
            ))}

            {/* Services dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                onClick={() => { setServicesOpen(o => !o); setResourcesOpen(false); }}
                aria-expanded={servicesOpen}
                className={`${linkCls(servicesActive)} flex items-center gap-1`}
              >
                Services
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl border border-[#e8eaea] shadow-xl p-1.5 z-50 animate-slide-down">
                  {SERVICES_ITEMS.map(item => (
                    <Link key={item.href} to={item.href} className={dropdownLinkCls(isActive(item.href))}>
                      <i className={`${item.icon} text-[#2D7F3E] text-base`} />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Resources dropdown */}
            <div ref={resourcesRef} className="relative">
              <button
                onClick={() => { setResourcesOpen(o => !o); setServicesOpen(false); }}
                aria-expanded={resourcesOpen}
                className={`${linkCls(resourcesActive)} flex items-center gap-1`}
              >
                Resources
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl border border-[#e8eaea] shadow-xl p-1.5 z-50 animate-slide-down">
                  {RESOURCE_ITEMS.map(item => (
                    <Link key={item.href} to={item.href} className={dropdownLinkCls(isActive(item.href))}>
                      <i className={`${item.icon} text-[#2D7F3E] text-base`} />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/contact" className={linkCls(isActive('/contact'))}>Contact</Link>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <a
              href="tel:+27102240406"
              className="text-sm font-medium text-[#6b7280] hover:text-[#2D7F3E] transition-colors"
            >
              <i className="ri-phone-line mr-1.5" />+27 10 224 0406
            </a>
            <Link to="/contact" className="btn btn-primary btn-sm">Get Started</Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="lg:hidden p-2 rounded-lg transition-colors text-[#3d3f42] hover:bg-[#f8f9fa]"
            aria-label="Toggle menu"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile backdrop */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 top-[68px] bg-black/30 z-40" onClick={() => setMenuOpen(false)} />
      )}

      {/* Mobile panel */}
      <div className={`lg:hidden fixed top-[68px] inset-x-0 z-50 bg-white border-t border-[#e8eaea] shadow-2xl transition-all duration-300 ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'}`}>
        <nav className="px-4 py-5 space-y-0.5 max-h-[80vh] overflow-y-auto">
          {[...NAV, ...SERVICES_ITEMS, ...RESOURCE_ITEMS, { label: 'Contact', href: '/contact' }].map(item => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMenuOpen(false)}
              className={`flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive(item.href) ? 'bg-[#f0f9f2] text-[#2D7F3E] font-semibold' : 'text-[#3d3f42] hover:bg-[#f8f9fa]'
              }`}
            >
              {isActive(item.href) && <i className="ri-checkbox-circle-fill text-[#2D7F3E] mr-2 text-sm" />}
              {item.label}
            </Link>
          ))}
          <div className="pt-4 pb-2 px-3">
            <Link to="/contact" onClick={() => setMenuOpen(false)}>
              <button className="btn btn-primary w-full">Get Started</button>
            </Link>
          </div>
          <div className="px-3 pb-3 pt-1 flex items-center gap-2 text-sm text-[#6b7280]">
            <i className="ri-phone-line text-[#2D7F3E]" />
            <a href="tel:+27102240406">+27 10 224 0406</a>
          </div>
        </nav>
      </div>
    </header>
  );
}
